/**
 * Spend limits — the org's monthly cap, and each member's.
 *
 * Ported from `bimgo-ai-web`'s `SpendLimitsCards`. See
 * `bimgo-ai-api-v2/docs/organization-spend-limits.md`. Two things to keep in
 * mind when editing this file:
 *
 * 1. **`null` means "no cap / inherit"; `0` is a real cap meaning "may spend
 *    nothing".** They stay distinguishable end to end, which is why every input
 *    holds a string until submit and why nothing here writes `value || null`
 *    (0 is falsy and would silently become "no cap").
 * 2. **Both surfaces are read-only at rest; every edit happens in a dialog.**
 *    These are live spending caps, and an always-visible input invited stray
 *    edits — and, in the narrow desktop pane, widened the table enough to
 *    scroll sideways.
 *
 * `canEdit` is `org:manage_limits`. Frontend gating is UX: api-v2 refuses the
 * write regardless, and a control that is offered and then refused is worse
 * than one that was never offered.
 */
import { useEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { API_BASE_URL, isConfigured } from "../../lib/config";
import {
  formatCredits,
  getMemberLimits,
  limitToInput,
  parseLimitInput,
  updateAllMemberLimits,
  updateMemberLimit,
  updateOrganizationLimits,
  type BillingMode,
  type MemberLimit,
  type OrganizationLimits,
} from "../../lib/tokens";
import { Modal } from "../settings/Modal";
import { LoadingBlock } from "../settings/Spinner";
import { EmptyRow, ErrorRow, LimitInput, SlidersIcon, useNumberLocale } from "./shared";

/**
 * The org cap's **edit affordance only** — the value itself is one of the plan
 * card's fields, so it uses that card's grid like every other figure. Keeping
 * the button here and the number there means the two cannot drift apart, and
 * the button always edits the figure the reader is looking at.
 */
export function OrgLimitEditButton({
  orgId,
  token,
  limits,
  canEdit,
  onChange,
}: {
  orgId: string;
  token: string | null;
  limits: OrganizationLimits | null;
  canEdit: boolean;
  onChange: (next: OrganizationLimits) => void;
}) {
  const { t } = useLocale();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // A computed default opens as an empty box: the value is derived, so
  // pre-filling it would silently promote it to an explicit setting on save.
  const draftFor = (data: OrganizationLimits) =>
    data.monthly_credit_limit_is_default ? "" : limitToInput(data.monthly_credit_limit);

  // Reset from the stored value on every open, so a cancelled edit never leaks
  // into the next one.
  const open = () => {
    if (limits) setDraft(draftFor(limits));
    setSaveError(null);
    setEditing(true);
  };

  async function save() {
    const parsed = parseLimitInput(draft);
    if (!parsed.ok) {
      setSaveError(t("limits.invalid"));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateOrganizationLimits(token, orgId, parsed.value, {
        apiBaseUrl: API_BASE_URL(),
      });
      onChange(updated);
      setDraft(draftFor(updated));
      setEditing(false); // only on success, so a failed save keeps what was typed
    } catch {
      setSaveError(t("limits.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit || !limits) return null;

  return (
    <>
      <button type="button" className="settings-btn secondary btn-sm" onClick={open}>
        {t("limits.edit")}
      </button>

      <Modal
        open={editing}
        onClose={() => (saving ? undefined : setEditing(false))}
        closable={!saving}
        title={t("limits.org.editTitle")}
        footer={
          <>
            <button
              type="button"
              className="settings-btn secondary"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              {t("limits.cancel")}
            </button>
            <button type="button" className="settings-btn" onClick={save} disabled={saving}>
              {saving ? t("limits.saving") : t("limits.save")}
            </button>
          </>
        }
      >
        <p className="settings-note">{t("limits.org.hint")}</p>
        <LimitInput
          value={draft}
          onChange={setDraft}
          placeholder={t("limits.org.placeholder")}
          disabled={saving}
          label={t("limits.org.title")}
        />
        <p className="settings-note">{t("limits.org.clearHint")}</p>
        {saveError ? <ErrorRow message={saveError} /> : null}
      </Modal>
    </>
  );
}

/**
 * Every member's cap and their wallet spend this cycle.
 *
 * The card is read-only; the whole set is edited in one dialog behind the
 * header button, and **only the rows that actually changed are written** — so
 * saving an untouched dialog is a no-op rather than N redundant writes.
 */
export function MemberLimitsCard({
  orgId,
  token,
  canEdit,
}: {
  orgId: string;
  token: string | null;
  canEdit: boolean;
}) {
  const { t } = useLocale();
  const numberLocale = useNumberLocale();

  const [rows, setRows] = useState<MemberLimit[]>([]);
  // Which plan mode this org is on, so the dialog explains only the path that
  // applies to it. It arrives on the member-limits response itself, so this
  // costs no second request.
  const [mode, setMode] = useState<BillingMode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [bulkDraft, setBulkDraft] = useState("");
  const [bulkApplied, setBulkApplied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Read at request time, not depended on: the session guard rotates the token
  // on every window focus, and a token dependency re-ran this fetch — skeleton
  // and all — on each refocus. The same rule `usePermissions` follows.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!orgId || !isConfigured()) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMemberLimits(tokenRef.current, orgId, { apiBaseUrl: API_BASE_URL() })
      .then((result) => {
        if (cancelled) return;
        setRows(result.members);
        setMode(result.billing_mode);
      })
      .catch(() => !cancelled && setError(t("wallet.loadFailed")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Seed the drafts from the stored values each time the dialog opens, so a
  // cancelled edit never leaks into the next one.
  const open = () => {
    setDrafts(Object.fromEntries(rows.map((r) => [r.user_id, limitToInput(r.member_limit)])));
    setBulkDraft("");
    setBulkApplied(false);
    setSaveError(null);
    setEditing(true);
  };

  /** Fill every row locally. Nothing is written until Save. */
  const applyToAll = () => {
    const parsed = parseLimitInput(bulkDraft);
    if (!parsed.ok) {
      setSaveError(t("limits.invalid"));
      return;
    }
    setSaveError(null);
    setDrafts(Object.fromEntries(rows.map((r) => [r.user_id, bulkDraft.trim()])));
    setBulkApplied(true);
  };

  async function save() {
    const parsedByUser: Record<string, number | null> = {};
    for (const r of rows) {
      const parsed = parseLimitInput(drafts[r.user_id] ?? "");
      if (!parsed.ok) {
        setSaveError(t("limits.invalid"));
        return;
      }
      parsedByUser[r.user_id] = parsed.value;
    }

    const changed = rows.filter((r) => parsedByUser[r.user_id] !== r.member_limit);
    if (changed.length === 0) {
      setEditing(false);
      return;
    }

    setSaving(true);
    setSaveError(null);
    const deps = { apiBaseUrl: API_BASE_URL() };
    try {
      if (bulkApplied && changed.length === rows.length && rows[0]) {
        // "Apply to all" collapses to the batch endpoint: one request rather
        // than one per member.
        setRows(
          await updateAllMemberLimits(
            tokenRef.current,
            orgId,
            parsedByUser[rows[0].user_id] ?? null,
            deps,
          ),
        );
      } else {
        const updates = await Promise.all(
          changed.map((r) =>
            updateMemberLimit(
              tokenRef.current,
              orgId,
              r.user_id,
              parsedByUser[r.user_id] ?? null,
              deps,
              // The PUT answers with a single-member shape that carries no
              // email, so the one already on screen is kept.
            ).then((updated) => ({ ...updated, email: r.email })),
          ),
        );
        const byId = new Map(updates.map((u) => [u.user_id, u]));
        setRows((prev) => prev.map((r) => byId.get(r.user_id) ?? r));
      }
      setEditing(false); // only on success, so nothing typed is lost
    } catch {
      setSaveError(t("limits.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  /** Null is unlimited — never 0, which is a real cap of "may spend nothing". */
  const capText = (r: MemberLimit) =>
    r.effective_limit === null ? t("limits.unlimited") : formatCredits(r.effective_limit, numberLocale);

  return (
    <div className="settings-card">
      <div className="card-head-row">
        <h3 className="card-title">
          <span className="card-title-icon">
            <SlidersIcon />
          </span>
          {t("limits.members.title")}
        </h3>
        {canEdit && !loading && !error && rows.length > 0 ? (
          <button type="button" className="settings-btn secondary btn-sm" onClick={open}>
            {t("limits.edit")}
          </button>
        ) : null}
      </div>

      {loading ? (
        <LoadingBlock size={24} />
      ) : error ? (
        <ErrorRow message={error} />
      ) : rows.length === 0 ? (
        <EmptyRow message={t("limits.members.empty")} />
      ) : (
        <div className="members-scroll">
          <table className="members-table">
            <thead>
              <tr>
                <th>{t("limits.members.colMember")}</th>
                {/* Spent first, then the cap it counts against — the same pair,
                    in the same order, as the usage dashboard's credit line, so
                    the two pages state one number rather than two that need
                    reconciling. Both are wallet figures: a seat member still
                    inside their prepaid allowance reads 0 here. */}
                <th>{t("limits.members.colSpent")}</th>
                <th>{t("limits.members.colCap")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id}>
                  <td className="member-email">
                    {r.email ?? r.user_id}
                    {r.exhausted ? (
                      <span className="wallet-badge is-danger">{t("limits.members.exhausted")}</span>
                    ) : null}
                  </td>
                  <td className="num">{formatCredits(r.spent, numberLocale)}</td>
                  <td className="num">{capText(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={editing}
        onClose={() => (saving ? undefined : setEditing(false))}
        closable={!saving}
        wide
        title={t("limits.members.editTitle")}
        description={t("limits.members.hint")}
        footer={
          <>
            <button
              type="button"
              className="settings-btn secondary"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              {t("limits.cancel")}
            </button>
            <button type="button" className="settings-btn" onClick={save} disabled={saving}>
              {saving ? t("limits.saving") : t("limits.save")}
            </button>
          </>
        }
      >
        {/* The cap is one mechanism in both modes; they differ only in what is
            spent before it, so only the path this org is actually on is named —
            including what a blank box means, which differs by mode: 0 on
            per_seat, no per-member ceiling on org_shared. */}
        {mode ? (
          <p className="settings-note">
            {mode === "org_shared"
              ? t("limits.members.hintOrgShared")
              : t("limits.members.hintPerSeat")}{" "}
            {mode === "org_shared"
              ? t("limits.members.blankOrgShared")
              : t("limits.members.blankPerSeat")}
          </p>
        ) : null}

        <div className="limit-bulk">
          <span className="settings-note">{t("limits.members.applyAll")}</span>
          <LimitInput
            value={bulkDraft}
            onChange={setBulkDraft}
            placeholder={t("limits.members.applyAllPlaceholder")}
            disabled={saving}
            label={t("limits.members.applyAll")}
          />
          <button
            type="button"
            className="settings-btn secondary btn-sm"
            onClick={applyToAll}
            disabled={saving}
          >
            {t("limits.members.applyAllAction")}
          </button>
        </div>

        <div className="limit-rows">
          {rows.map((r) => (
            <div className="limit-row" key={r.user_id}>
              <span className="limit-row-name" title={r.email ?? r.user_id}>
                {r.email ?? r.user_id}
              </span>
              <LimitInput
                value={drafts[r.user_id] ?? ""}
                onChange={(v) => setDrafts((prev) => ({ ...prev, [r.user_id]: v }))}
                placeholder={t("limits.members.inherit")}
                disabled={saving}
                label={r.email ?? r.user_id}
              />
            </div>
          ))}
        </div>

        {saveError ? <ErrorRow message={saveError} /> : null}
      </Modal>
    </div>
  );
}
